import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Import only the d3 submodules we use. Importing the `d3` umbrella pulls in
// `@types/d3` → `@types/d3-dispatch`, whose `const` type-parameter syntax is
// TS 5.0+ and fails to parse under this project's TypeScript 4.9.
import {
  hierarchy as d3Hierarchy,
  tree as d3Tree,
  type HierarchyPointNode,
  type HierarchyPointLink,
} from 'd3-hierarchy';
import { linkRadial as d3LinkRadial } from 'd3-shape';
import type { CollaboratorLaneStatus, CollaboratorProspect } from '../../types/collaboratorProspect';

export type InquiryNodeKind =
  | 'root'
  | 'customer'
  | 'topic'
  | 'sku'
  | 'note'
  | 'lane'
  | 'class'
  | 'filter';

export interface InquiryGraphNodeProduct {
  sku: string;
  name?: string;
  image?: string;
  classDescription?: string;
  subClass?: string;
  stdCost?: number;
  variantCount?: number;
}

export interface InquiryGraphNode {
  id: string;
  kind: InquiryNodeKind;
  label: string;
  parentId: string | null;
  childIds: string[];
  pinned?: boolean;
  prospect?: CollaboratorProspect;
  laneStatus?: CollaboratorLaneStatus;
  searchQuery?: string;
  rationale?: string;
  query?: string;
  productCount?: number;
  classDescription?: string;
  products?: InquiryGraphNodeProduct[];
  sku?: string;
  // filter node — a resolved constraint applied to its parent + descendants.
  filterTerm?: string;
  filterConfidence?: number;
  createdAt: number;
}

export interface NodeMetrics {
  numProducts: number;
  numItems: number;
  estCost: number;
}

export interface InquiryGraphState {
  rootId: string;
  nodes: Record<string, InquiryGraphNode>;
}

interface TreeDatum {
  id: string;
  node: InquiryGraphNode;
  children?: TreeDatum[];
  hiddenSkuCount: number;
  skuChildCount: number;
}

export interface InquiryFocusGraphD3Props {
  graph: InquiryGraphState;
  focusedNodeId: string;
  expandedTopicIds: Set<string>;
  onFocusNode: (id: string) => void;
  onToggleTopicExpanded: (topicId: string) => void;
  onSkipLane?: (laneId: string) => void;
  /**
   * Reparent a `filter` node onto a new target (drag-and-drop). Dragging a
   * filter node to the root makes it apply inquiry-wide.
   */
  onReparentNode?: (childId: string, newParentId: string) => void;
  /** Own + inherited cost/count metrics keyed by node id. */
  metrics?: Record<string, NodeMetrics>;
}

const NODE_DIMS: Record<
  InquiryNodeKind,
  { w: number; h: number; rx: number }
> = {
  root: { w: 88, h: 28, rx: 6 },
  customer: { w: 168, h: 36, rx: 8 },
  topic: { w: 132, h: 30, rx: 6 },
  lane: { w: 118, h: 30, rx: 6 },
  sku: { w: 76, h: 24, rx: 5 },
  note: { w: 108, h: 28, rx: 6 },
  class: { w: 140, h: 32, rx: 7 },
  filter: { w: 124, h: 26, rx: 13 },
};

function formatMoneyShort(n: number): string {
  if (!n) return '$0';
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Pan/zoom viewport — the SVG is a fixed-height window the user navigates,
// rather than a content-sized box that overflows into a tiny scroll area.
const VIEWPORT_HEIGHT = 340;
const MIN_ZOOM = 0.35;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 1.2;

// Half-radial fan: root sits at top-center and children sweep a 180° arc
// downward. A downward semicircle is ~2:1 wide-to-tall, so it fills the wide,
// short chat column while keeping vertical takeover to a single ring's depth.
const FAN_SPAN = Math.PI; // angular extent (radians) the children occupy
const FAN_OFFSET = Math.PI / 2; // rotate the span so it faces straight down
const RING_MIN_GAP = 122; // minimum radial distance between successive rings
const NODE_ARC_BUDGET = 122; // approx arc length (px) reserved per sibling

interface ZoomTransform {
  k: number;
  x: number;
  y: number;
}

function truncateLabel(label: string, max = 22): string {
  const t = (label || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function buildTreeData(
  graph: InquiryGraphState,
  expandedTopicIds: Set<string>
): TreeDatum | null {
  const root = graph.nodes[graph.rootId];
  if (!root) return null;

  const visit = (id: string): TreeDatum => {
    const node = graph.nodes[id]!;
    const skuChildIds = node.childIds.filter((cid) => graph.nodes[cid]?.kind === 'sku');
    // Bookmarked SKU children stay COLLAPSED under any node until expanded.
    const showSkus = expandedTopicIds.has(id);
    const visibleChildIds = node.childIds.filter((cid) => {
      const child = graph.nodes[cid];
      if (!child) return false;
      if (child.kind === 'sku' && !showSkus) return false;
      return true;
    });
    const children = visibleChildIds.map(visit);
    const hiddenSkuCount = !showSkus ? skuChildIds.length : 0;
    return {
      id,
      node,
      children: children.length > 0 ? children : undefined,
      hiddenSkuCount,
      skuChildCount: skuChildIds.length,
    };
  };

  return visit(graph.rootId);
}

export default function InquiryFocusGraphD3({
  graph,
  focusedNodeId,
  expandedTopicIds,
  onFocusNode,
  onToggleTopicExpanded,
  onSkipLane,
  onReparentNode,
  metrics = {},
}: InquiryFocusGraphD3Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(320);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth || 320);
    return () => ro.disconnect();
  }, []);

  const rootDatum = useMemo(
    () => buildTreeData(graph, expandedTopicIds),
    [graph, expandedTopicIds]
  );

  const layout = useMemo(() => {
    if (!rootDatum) return null;

    const hierarchyRoot = d3Hierarchy<TreeDatum>(rootDatum, (d) => d.children)
      .sum(() => 1);

    // Half-radial fan: the angular coordinate (x) is laid out over FAN_SPAN
    // and then rotated by FAN_OFFSET so children sweep a 180° arc *downward*
    // from a top-centered root.

    // Size each ring from its own level's breadth so wide labelled pills have
    // enough arc length and don't overlap. Arc length at radius r over the fan
    // span is r · FAN_SPAN, so a ring with N siblings needs
    // r ≥ N · NODE_ARC_BUDGET / FAN_SPAN. Rings are kept monotonically
    // increasing with a minimum gap so deeper levels never collide inward.
    const depthCounts = new Map<number, number>();
    hierarchyRoot.each((n) =>
      depthCounts.set(n.depth, (depthCounts.get(n.depth) || 0) + 1)
    );
    const ringRadius = new Map<number, number>();
    ringRadius.set(0, 0);
    let prevR = 0;
    Array.from(depthCounts.keys())
      .filter((d) => d > 0)
      .sort((a, b) => a - b)
      .forEach((depth) => {
        const breadth = depthCounts.get(depth) || 1;
        const need = (breadth * NODE_ARC_BUDGET) / FAN_SPAN;
        const r = Math.max(prevR + RING_MIN_GAP, need, RING_MIN_GAP);
        ringRadius.set(depth, r);
        prevR = r;
      });
    const maxRadius = Math.max(RING_MIN_GAP, ...Array.from(ringRadius.values()));

    const tree = d3Tree<TreeDatum>()
      .size([FAN_SPAN, maxRadius])
      .separation(
        (a, b) => (a.parent === b.parent ? 1 : 2) / Math.max(a.depth, 1)
      );
    tree(hierarchyRoot);

    // After tree(), x = angle within [0, FAN_SPAN]. Override the radius with our
    // per-ring scale (size()'s linear radius doesn't honour the gap rules).
    const nodes = hierarchyRoot.descendants() as HierarchyPointNode<TreeDatum>[];
    nodes.forEach((n) => {
      n.y = ringRadius.get(n.depth) ?? 0;
    });
    const links = hierarchyRoot.links() as HierarchyPointLink<TreeDatum>[];

    // Polar → cartesian, matching d3's pointRadial (used by linkRadial). The
    // FAN_OFFSET rotation makes the arc face downward.
    const angleOf = (d: HierarchyPointNode<TreeDatum>) => d.x + FAN_OFFSET;
    const pos = (d: HierarchyPointNode<TreeDatum>) => {
      const a = angleOf(d);
      return { px: d.y * Math.sin(a), py: -d.y * Math.cos(a) };
    };

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const d of nodes) {
      const { px, py } = pos(d);
      const dims = NODE_DIMS[d.data.node.kind] || NODE_DIMS.topic;
      minX = Math.min(minX, px - dims.w / 2 - 6);
      maxX = Math.max(maxX, px + dims.w / 2 + 46); // right-side affordances
      minY = Math.min(minY, py - dims.h / 2 - 6);
      maxY = Math.max(maxY, py + dims.h / 2 + 6);
    }
    const pad = 14;
    const contentWidth = maxX - minX + pad * 2;
    const contentHeight = maxY - minY + pad * 2;
    // Offset that maps the content's top-left to the origin.
    const cx = -minX + pad;
    const cy = -minY + pad;

    const linkGen = d3LinkRadial<
      HierarchyPointLink<TreeDatum>,
      HierarchyPointNode<TreeDatum>
    >()
      .angle((d) => angleOf(d))
      .radius((d) => d.y);

    return {
      nodes,
      links,
      linkGen,
      contentWidth,
      contentHeight,
      cx,
      cy,
      pos,
    };
  }, [rootDatum]);

  // ── Pan / zoom ──────────────────────────────────────────────────────────
  const svgRef = useRef<SVGSVGElement>(null);
  const [transform, setTransform] = useState<ZoomTransform>({ k: 1, x: 0, y: 0 });
  const interactedRef = useRef(false);
  const dragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(
    null
  );

  const fitTransform = useCallback(
    (contentW: number, contentH: number): ZoomTransform => {
      const vw = containerWidth || 320;
      const vh = VIEWPORT_HEIGHT;
      const k = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, Math.min(vw / contentW, vh / contentH, 1))
      );
      // Center the radial graph in both axes.
      const x = Math.max(0, (vw - contentW * k) / 2);
      const y = Math.max(0, (vh - contentH * k) / 2);
      return { k, x, y };
    },
    [containerWidth]
  );

  const fitToView = useCallback(() => {
    if (!layout) return;
    interactedRef.current = false;
    setTransform(fitTransform(layout.contentWidth, layout.contentHeight));
  }, [layout, fitTransform]);

  // Auto-fit on first render / structural changes until the user interacts.
  useEffect(() => {
    if (!layout || interactedRef.current) return;
    setTransform(fitTransform(layout.contentWidth, layout.contentHeight));
  }, [layout, fitTransform]);

  const zoomBy = useCallback(
    (factor: number, cx?: number, cy?: number) => {
      interactedRef.current = true;
      setTransform((t) => {
        const k = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, t.k * factor));
        if (k === t.k) return t;
        const vw = containerWidth || 320;
        const px = cx ?? vw / 2;
        const py = cy ?? VIEWPORT_HEIGHT / 2;
        // Keep the point under (px,py) stationary while scaling.
        return {
          k,
          x: px - ((px - t.x) / t.k) * k,
          y: py - ((py - t.y) / t.k) * k,
        };
      });
    },
    [containerWidth]
  );

  // Native wheel listener so we can preventDefault (React onWheel is passive).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      zoomBy(factor, e.clientX - rect.left, e.clientY - rect.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    // Only pan from empty canvas — let node clicks through.
    const target = e.target as Element;
    if (target.closest('.ps-collaborator__graph-node')) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      ox: 0,
      oy: 0,
    };
    interactedRef.current = true;
    setTransform((t) => {
      dragRef.current!.ox = t.x;
      dragRef.current!.oy = t.y;
      return t;
    });
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    setTransform((t) => ({ ...t, x: d.ox + dx, y: d.oy + dy }));
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const handleNodeClick = useCallback(
    (id: string, kind: InquiryNodeKind) => {
      if (kind === 'sku') {
        onFocusNode(id);
        return;
      }
      onFocusNode(id);
    },
    [onFocusNode]
  );

  // ── Filter-node drag-to-reparent ─────────────────────────────────────────
  const nodeDragRef = useRef<{ id: string; moved: boolean } | null>(null);
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  const [hoverTargetId, setHoverTargetId] = useState<string | null>(null);

  // Map a screen point back into content coords and find the node under it.
  const hitTestNode = useCallback(
    (clientX: number, clientY: number): string | null => {
      const svg = svgRef.current;
      if (!svg || !layout) return null;
      const rect = svg.getBoundingClientRect();
      const contentX = (clientX - rect.left - transform.x) / transform.k - layout.cx;
      const contentY = (clientY - rect.top - transform.y) / transform.k - layout.cy;
      for (const d of layout.nodes) {
        const { px, py } = layout.pos(d);
        const dims = NODE_DIMS[d.data.node.kind] || NODE_DIMS.topic;
        if (
          Math.abs(contentX - px) <= dims.w / 2 + 4 &&
          Math.abs(contentY - py) <= dims.h / 2 + 4
        ) {
          return d.data.node.id;
        }
      }
      return null;
    },
    [layout, transform]
  );

  const isValidDropTarget = useCallback(
    (dragId: string, targetId: string | null): boolean => {
      if (!targetId || targetId === dragId) return false;
      const target = graph.nodes[targetId];
      const dragged = graph.nodes[dragId];
      if (!target || !dragged) return false;
      if (target.kind === 'filter' || target.kind === 'sku') return false;
      if (dragged.parentId === targetId) return false; // already there
      return true;
    },
    [graph.nodes]
  );

  const onFilterPointerDown = useCallback(
    (e: React.PointerEvent<SVGGElement>, id: string) => {
      if (!onReparentNode) return;
      e.stopPropagation();
      nodeDragRef.current = { id, moved: false };
      setDragNodeId(id);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [onReparentNode]
  );

  const onFilterPointerMove = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      const drag = nodeDragRef.current;
      if (!drag) return;
      e.stopPropagation();
      drag.moved = true;
      const t = hitTestNode(e.clientX, e.clientY);
      setHoverTargetId(isValidDropTarget(drag.id, t) ? t : null);
    },
    [hitTestNode, isValidDropTarget]
  );

  const onFilterPointerUp = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      const drag = nodeDragRef.current;
      nodeDragRef.current = null;
      setDragNodeId(null);
      const target = hoverTargetId;
      setHoverTargetId(null);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (!drag) return;
      e.stopPropagation();
      if (drag.moved && onReparentNode && isValidDropTarget(drag.id, target)) {
        onReparentNode(drag.id, target!);
      } else if (!drag.moved) {
        onFocusNode(drag.id); // treat a no-move press as a click → focus
      }
    },
    [hoverTargetId, onReparentNode, isValidDropTarget, onFocusNode]
  );

  if (!layout || !rootDatum) {
    return (
      <div ref={containerRef} className="ps-collaborator__graph-canvas ps-collaborator__graph-canvas--d3">
        <p className="ps-collaborator__graph-empty">No inquiry nodes yet.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="ps-collaborator__graph-canvas ps-collaborator__graph-canvas--d3">
      <div className="ps-collaborator__graph-viewport">
        <svg
          ref={svgRef}
          className="ps-collaborator__graph-svg"
          width={containerWidth}
          height={VIEWPORT_HEIGHT}
          role="tree"
          aria-label="Inquiry focus graph"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <g
            transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}
          >
          <g transform={`translate(${layout.cx},${layout.cy})`}>
        <g className="ps-collaborator__graph-links">
          {layout.links.map((link) => (
            <path
              key={`${link.source.data.id}-${link.target.data.id}`}
              className="ps-collaborator__graph-edge"
              d={layout.linkGen(link) || undefined}
            />
          ))}
        </g>
        <g className="ps-collaborator__graph-nodes">
          {layout.nodes.map((d) => {
            const { node, hiddenSkuCount, skuChildCount } = d.data;
            const isTopicExpanded = expandedTopicIds.has(node.id);
            const dims = NODE_DIMS[node.kind] || NODE_DIMS.topic;
            const { px: x, py: y } = layout.pos(d);
            const halfW = dims.w / 2;
            const isFocused = node.id === focusedNodeId;
            // ★ marks nodes that carry bookmarked products (SKU children or
            // legacy products metadata) — not "pinned".
            const isPinned = skuChildCount > 0 || (node.products?.length || 0) > 0;
            const laneStatus = node.laneStatus || 'pending';
            const label = truncateLabel(
              node.kind === 'sku' ? node.sku || node.label : node.label,
              node.kind === 'customer' ? 28 : node.kind === 'sku' ? 10 : 20
            );
            const m = metrics[node.id];
            const showMetrics =
              !!m &&
              node.kind !== 'sku' &&
              node.kind !== 'note' &&
              node.kind !== 'filter' &&
              (m.numProducts > 0 || m.estCost > 0);
            const isFilter = node.kind === 'filter';
            const isDropTarget = hoverTargetId === node.id;
            const isDragging = dragNodeId === node.id;
            const metricsLine = showMetrics
              ? `${formatMoneyShort(m!.estCost)} · ${m!.numProducts}p`
              : '';
            const classProductCount = node.products?.length || 0;

            return (
              <g
                key={node.id}
                className={
                  `ps-collaborator__graph-node` +
                  ` ps-collaborator__graph-node--${node.kind}` +
                  (isFocused ? ' is-focused' : '') +
                  (isPinned ? ' is-pinned' : '') +
                  (isFilter ? ' is-draggable' : '') +
                  (isDragging ? ' is-dragging' : '') +
                  (isDropTarget ? ' is-drop-target' : '') +
                  (node.kind === 'lane' ? ` ps-collaborator__graph-node--lane-${laneStatus}` : '')
                }
                transform={`translate(${x},${y})`}
                onClick={isFilter ? undefined : () => handleNodeClick(node.id, node.kind)}
                onPointerDown={isFilter ? (e) => onFilterPointerDown(e, node.id) : undefined}
                onPointerMove={isFilter ? onFilterPointerMove : undefined}
                onPointerUp={isFilter ? onFilterPointerUp : undefined}
                role="treeitem"
                aria-selected={isFocused}
                aria-label={node.label}
              >
                <title>
                  {isFilter
                    ? `Filter: ${node.filterTerm || node.label}${
                        node.filterConfidence && node.filterConfidence < 1
                          ? ` (${Math.round(node.filterConfidence * 100)}% match)`
                          : ''
                      } — drag onto a node to scope it (drag to root for all)`
                    : node.rationale || node.searchQuery || node.label}
                </title>
                <rect
                  className="ps-collaborator__graph-node-rect"
                  x={-halfW}
                  y={-dims.h / 2}
                  width={dims.w}
                  height={dims.h}
                  rx={dims.h / 2}
                  ry={dims.h / 2}
                />
                {isPinned && (
                  <text
                    className="ps-collaborator__graph-node-pin"
                    x={-halfW + 8}
                    y={-dims.h / 2 + 11}
                  >
                    ★
                  </text>
                )}
                <text
                  className="ps-collaborator__graph-node-label"
                  x={0}
                  y={showMetrics ? -2 : 4}
                  textAnchor="middle"
                >
                  {label}
                </text>
                {showMetrics && (
                  <text
                    className="ps-collaborator__graph-node-metrics"
                    x={0}
                    y={dims.h / 2 - 3}
                    textAnchor="middle"
                  >
                    {metricsLine}
                  </text>
                )}
                {(node.kind === 'topic' || node.kind === 'lane') &&
                  (node.productCount ?? 0) > 0 && (
                  <>
                    <rect
                      className="ps-collaborator__graph-node-badge"
                      x={halfW - 24}
                      y={-dims.h / 2 - 6}
                      width={20}
                      height={14}
                      rx={7}
                    />
                    <text
                      className="ps-collaborator__graph-node-badge-text"
                      x={halfW - 14}
                      y={-dims.h / 2 + 4}
                      textAnchor="middle"
                    >
                      {node.productCount}
                    </text>
                  </>
                )}
                {node.kind === 'lane' && (
                  <text
                    className="ps-collaborator__graph-node-lane-status"
                    x={halfW - 6}
                    y={dims.h / 2 + 11}
                    textAnchor="end"
                  >
                    {laneStatus.slice(0, 4)}
                  </text>
                )}
                {node.kind !== 'sku' && skuChildCount > 0 && (
                  <g
                    className="ps-collaborator__graph-node-chevron"
                    transform={`translate(${halfW + 4},${-8})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleTopicExpanded(node.id);
                    }}
                  >
                    <rect
                      className="ps-collaborator__graph-node-chevron-bg"
                      width={isTopicExpanded ? 28 : 36}
                      height={16}
                      rx={4}
                    />
                    <text
                      className="ps-collaborator__graph-node-chevron-text"
                      x={isTopicExpanded ? 14 : 18}
                      y={11}
                      textAnchor="middle"
                    >
                      {isTopicExpanded ? '▾' : `▸ ${hiddenSkuCount || skuChildCount}`}
                    </text>
                  </g>
                )}
                {classProductCount > 0 && skuChildCount === 0 && node.kind !== 'sku' && (
                  <g
                    className="ps-collaborator__graph-node-chevron"
                    transform={`translate(${halfW + 4},${-8})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleTopicExpanded(node.id);
                    }}
                  >
                    <rect
                      className="ps-collaborator__graph-node-chevron-bg"
                      width={isTopicExpanded ? 28 : 36}
                      height={16}
                      rx={4}
                    />
                    <text
                      className="ps-collaborator__graph-node-chevron-text"
                      x={isTopicExpanded ? 14 : 18}
                      y={11}
                      textAnchor="middle"
                    >
                      {isTopicExpanded ? '▾' : `▸ ${classProductCount}`}
                    </text>
                  </g>
                )}
                {node.kind === 'lane' &&
                  laneStatus !== 'skipped' &&
                  onSkipLane && (
                    <g
                      className="ps-collaborator__graph-node-skip"
                      transform={`translate(${halfW + 4},${-6})`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSkipLane(node.id);
                      }}
                    >
                      <rect
                        className="ps-collaborator__graph-node-skip-bg"
                        width={32}
                        height={16}
                        rx={4}
                      />
                      <text
                        className="ps-collaborator__graph-node-skip-text"
                        x={16}
                        y={11}
                        textAnchor="middle"
                      >
                        Skip
                      </text>
                    </g>
                  )}
              </g>
            );
          })}
        </g>
          </g>
          </g>
        </svg>
        <div className="ps-collaborator__graph-zoom" role="group" aria-label="Zoom controls">
          <button
            type="button"
            className="ps-collaborator__graph-zoom-btn"
            onClick={() => zoomBy(ZOOM_STEP)}
            title="Zoom in"
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            className="ps-collaborator__graph-zoom-btn"
            onClick={() => zoomBy(1 / ZOOM_STEP)}
            title="Zoom out"
            aria-label="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            className="ps-collaborator__graph-zoom-btn ps-collaborator__graph-zoom-btn--fit"
            onClick={fitToView}
            title="Fit to view"
            aria-label="Fit to view"
          >
            ⤢
          </button>
        </div>
      </div>
      {(() => {
        const total = metrics[graph.rootId];
        if (!total || total.numProducts === 0) return null;
        return (
          <div className="ps-collaborator__graph-totals">
            <span className="ps-collaborator__graph-totals-cost">
              {formatMoney(total.estCost)}
            </span>
            <span className="ps-collaborator__graph-totals-sep">·</span>
            <span>{total.numProducts} products</span>
          </div>
        );
      })()}
      {Object.values(graph.nodes)
        .filter(
          (n) =>
            expandedTopicIds.has(n.id) &&
            (n.products?.length || 0) > 0
        )
        .map((n) => (
          <div key={`exp-${n.id}`} className="ps-collaborator__graph-classlist">
            <div className="ps-collaborator__graph-classlist-head">
              {n.label}
              {metrics[n.id] && (
                <span className="ps-collaborator__graph-classlist-cost">
                  {formatMoney(metrics[n.id].estCost)} · {metrics[n.id].numProducts}p
                </span>
              )}
            </div>
            <ul className="ps-collaborator__graph-classlist-items">
              {(n.products || []).map((p) => (
                <li
                  key={p.sku}
                  className="ps-collaborator__graph-classlist-item"
                >
                  {p.image && (
                    <img
                      src={p.image}
                      alt=""
                      className="ps-collaborator__graph-classlist-thumb"
                      loading="lazy"
                    />
                  )}
                  <span className="ps-collaborator__graph-classlist-sku">
                    {p.sku}
                  </span>
                  <span className="ps-collaborator__graph-classlist-name">
                    {p.name}
                  </span>
                  {typeof p.stdCost === 'number' && p.stdCost > 0 && (
                    <span className="ps-collaborator__graph-classlist-price">
                      {formatMoney(p.stdCost)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}
